import { useState } from "react";
import { Languages, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "../../lib/api";
import { LANGS } from "../../lib/i18n";

const areaCls = "w-full rounded-xl border border-ink/15 bg-white p-4 text-ink outline-none focus:border-champagne";
const inputCls = "h-11 w-full rounded-xl border border-ink/15 bg-white px-4 text-ink outline-none focus:border-champagne";
const FIELDS = ["description", "version", "color"];

export const DescriptionTranslator = ({ form, set }) => {
  const [targets, setTargets] = useState(["en", "fr", "de", "es"]);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("en");
  const get = (f) => form[`${f}_i18n`] || {};
  const setTr = (f, lang, v) => set(`${f}_i18n`, { ...get(f), [lang]: v });
  const hasSource = FIELDS.some((f) => form[f]?.trim());
  const done = (lang) => FIELDS.some((f) => get(f)[lang]);

  const toggle = (c) => setTargets((t) => (t.includes(c) ? t.filter((x) => x !== c) : [...t, c]));

  const run = async () => {
    setBusy(true);
    try {
      const fields = Object.fromEntries(FIELDS.map((f) => [f, form[f] || ""]));
      const { data } = await http.post("/admin/translate", { fields, targets });
      FIELDS.forEach((f) => { if (data?.[f]) set(`${f}_i18n`, { ...get(f), ...data[f] }); });
      if (targets[0]) setTab(targets[0]);
      toast.success("Traduzione completata: controlla e salva");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="description-translator">
      <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={7} className={areaCls}
        placeholder="Optional, condizioni, tagliandi, garanzia…" data-testid="car-field-description" />
      <div className="mt-5 rounded-xl border border-ink/10 bg-pearl p-4">
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-600">Traduci descrizione, versione e colore in</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {LANGS.map((l) => (
            <button key={l.code} type="button" onClick={() => toggle(l.code)} data-testid={`translate-target-${l.code}`}
              className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${targets.includes(l.code) ? "border-ink bg-ink text-pearl" : "border-ink/15 bg-white text-slate-700"}`}>
              {l.name}
            </button>
          ))}
          <button type="button" onClick={run} disabled={busy || !hasSource || !targets.length}
            className="btn-gold ml-auto disabled:opacity-50" data-testid="translate-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Languages className="h-4 w-4" />} {busy ? "Traduzione…" : "Traduci"}
          </button>
        </div>
      </div>
      <div className="mt-5">
        <div className="flex flex-wrap gap-1 border-b border-ink/10">
          {LANGS.map((l) => (
            <button key={l.code} type="button" onClick={() => setTab(l.code)} data-testid={`translation-tab-${l.code}`}
              className={`-mb-px border-b-2 px-3 py-2 text-sm ${tab === l.code ? "border-champagne font-semibold text-ink" : "border-transparent text-slate-600"}`}>
              {l.label}{done(l.code) ? " ✓" : ""}
            </button>
          ))}
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <input value={get("version")[tab] || ""} onChange={(e) => setTr("version", tab, e.target.value)} className={inputCls}
            placeholder="Versione tradotta" data-testid={`translation-version-${tab}`} />
          <input value={get("color")[tab] || ""} onChange={(e) => setTr("color", tab, e.target.value)} className={inputCls}
            placeholder="Colore tradotto" data-testid={`translation-color-${tab}`} />
        </div>
        <textarea value={get("description")[tab] || ""} onChange={(e) => setTr("description", tab, e.target.value)} rows={6}
          className={`${areaCls} mt-3`} placeholder="Nessuna traduzione: verrà mostrato il testo originale" data-testid={`translation-text-${tab}`} />
      </div>
    </div>
  );
};
